import React, { useState } from "react";

/* ─────────────────────────────────────────
   DEFAULT METHODOLOGY (mRNA pipeline)
───────────────────────────────────────── */
const DEFAULT_METHODOLOGY = `RNA Extraction, Qualification, Library Preparation and Sequencing:

Total RNA from Rice Leaf Tissues were extracted using NucleoSpin RNA Extraction Kit (Macherey-Nagel, Germany) following the manufacturers protocol. RNase-free DNase enzyme was added during the extraction to avoid DNA contamination. The RNA samples were checked for integrity and quantity using Qubit 4 Fluorometer (Invitrogen, USA). RNA samples with qualified RIN values were used for library preparation using the TruSeq RNA Library Prep Kit (Illumina, Inc., USA). Total RNA was first subjected to polyA selection, wherein mRNA molecules bearing polyadenine (polyA) tails were selectively captured using oligo-dT probes. The enriched mRNA was then fragmented and converted into first- and second-strand cDNA. Subsequent steps included end repair and A-tailing to facilitate the ligation of Illumina adapters. The resulting mRNA libraries were quantified using the QuantStudio Real-Time PCR System (Thermo Fisher Scientific, USA) and the Qubit 4.0 Fluorometer (Invitrogen, USA). Insert size distribution was assessed using the Agilent 4200 TapeStation System (Agilent Technologies, USA). Libraries exhibiting an appropriate insert size of 300–350 bp and an effective concentration above 10 nM were pooled and subjected to paired-end sequencing (2 × 150 bp) on the Illumina NovaSeq X platform (Illumina, Inc., USA).

Bioinformatics Analysis:

The reads obtained in .fastq format are checked using the FastQC v0.12.1 tool. The low- quality reads and adapter sequences are removed using the tool TrimGalore v0.6.10. The reference IRGSP-1.0 is downloaded from the Ensembl database and indexed using Hisat2 v2.2.1. The preprocessed reads are mapped against the reference genome index using the Hisat2 aligner. The aligned reads then undergo the quantification process with the help of the tool HtSeq v2.0.3. The count values obtained are imported to Rstudio and normalized using the R package EdgeR v4.0.7. The differentially expressed genes are obtained using DEG analysis with EdgeR and Limma v3.58.1 package. The DEGs obtained are used for gene set enrichment analysis using gprofiler and Kobas servers.`;

/* ─────────────────────────────────────────
   INJECT DYNAMIC THEME STYLES
───────────────────────────────────────── */
function injectStyle(dark) {
  let s = document.getElementById("mrna-style");
  if (!s) {
    s = document.createElement("style");
    s.id = "mrna-style";
    document.head.appendChild(s);
  }
  s.textContent = getStyle(dark);
}

function getStyle(dark) {
  const bg = dark ? '#0a1628' : '#f4f6f8';
  const cardBg = dark ? '#0f2040' : '#fff';
  const cardBorder = dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb';
  const text = dark ? '#e8edf4' : '#111827';
  const textSub = dark ? '#8aaac8' : '#6b7280';
  const inputBg = dark ? '#162a50' : '#fff';
  const inputBorder = dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb';
  const dropBg = dark ? '#162a50' : '#f8faff';
  const dropHoverBg = dark ? '#1e3460' : '#eef2ff';
  const accent = '#6366f1';

  return `
@import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;600;700&display=swap');

.mrna-page *, .mrna-page *::before, .mrna-page *::after { box-sizing: border-box; }

.mrna-page {
  font-family: 'Barlow', sans-serif;
  background: ${bg};
  min-height: 100vh;
  padding: 0 0 80px;
  color: ${text};
}

.mrna-page .mrna-breadcrumb {
  display: flex; align-items: center; gap: 6px;
  padding: 14px 32px;
  font-size: 13px; color: ${textSub};
  border-bottom: 1px solid ${cardBorder};
  background: ${cardBg};
}
.mrna-page .mrna-breadcrumb .bc-active { color: ${accent}; font-weight: 600; }
.mrna-page .mrna-breadcrumb .bc-sep { color: ${textSub}; }

.mrna-page .mrna-content {
  max-width: 960px;
  margin: 0 auto;
  padding: 28px 16px;
  display: flex; flex-direction: column; gap: 20px;
}

.mrna-page .section-card {
  background: ${cardBg};
  border-radius: 12px;
  border: 1px solid ${cardBorder};
  padding: 28px 32px;
  box-shadow: ${dark ? '0 4px 20px rgba(0,0,0,0.25)' : '0 1px 4px rgba(0,0,0,0.06)'};
}

.mrna-page .section-header {
  display: flex; align-items: flex-start; gap: 14px;
  margin-bottom: 24px;
}
.mrna-page .section-header-text h3 { margin: 0 0 3px; font-size: 19px; font-weight: 800; color: ${dark ? '#f8fafc' : '#111827'}; }
.mrna-page .section-header-text p  { margin: 0; font-size: 13px; color: ${textSub}; }

.mrna-page .divider { height: 1px; background: ${cardBorder}; margin: 20px 0; }

.mrna-page .sub-label {
  font-size: 13px; font-weight: 800;
  text-transform: uppercase; letter-spacing: .8px;
  color: ${accent}; margin-bottom: 12px;
  padding-bottom: 6px; border-bottom: 1.5px solid ${dark ? 'rgba(99,102,241,0.25)' : '#e0e7ff'};
}

.mrna-page .g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
@media(max-width:768px){ .mrna-page .g2 { grid-template-columns: 1fr; } }

.mrna-page .field { display: flex; flex-direction: column; gap: 5px; }
.mrna-page .field > label {
  font-size: 13px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .5px; color: ${dark ? '#8aaac8' : '#374151'};
}
.mrna-page .field > label .req { color: #dc2626; margin-left: 2px; }
.mrna-page .field input, .mrna-page .field select {
  padding: 10px 14px;
  border: 1.5px solid ${inputBorder}; border-radius: 8px;
  font-size: 14px; font-family: 'Barlow', sans-serif;
  color: ${text}; background: ${inputBg};
  outline: none; width: 100%;
  transition: border-color .18s, box-shadow .18s;
}
.field input:focus, .field select:focus {
  border-color: ${accent};
  box-shadow: 0 0 0 3px rgba(99,102,241,.18);
}
.field input::placeholder { color: ${dark ? '#475569' : '#c9cdd4'}; }

.sw-table-wrap { overflow-x: auto; margin-top: 4px; }
.sw-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sw-table th {
  background: ${accent}; color: #fff; font-weight: 700;
  font-size: 11px; padding: 8px; text-align: left; border: 1px solid ${dark ? 'rgba(255,255,255,0.1)' : '#4338ca'};
}
.sw-table td { padding: 6px 8px; border: 1px solid ${cardBorder}; vertical-align: middle; }
.sw-table tbody tr:nth-child(even) { background: ${dark ? '#132342' : '#f5f7ff'}; }
.sw-table input {
  width: 100%; padding: 8px 10px; border: 1.5px solid ${inputBorder};
  border-radius: 6px; font-size: 13px; font-family: 'Barlow', sans-serif;
  color: ${text}; background: ${inputBg}; outline: none;
  transition: border-color .18s, box-shadow .18s;
}
.sw-table input:focus {
  border-color: ${accent};
  box-shadow: 0 0 0 3px rgba(99,102,241,.18);
}
.sw-table input::placeholder { color: ${dark ? '#475569' : '#c9cdd4'}; }
.sw-remove-btn {
  background: none; border: none; cursor: pointer; color: #dc2626;
  font-size: 16px; padding: 4px 8px; border-radius: 4px;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.sw-remove-btn:hover { background: rgba(220,38,38,0.15); }
.btn-add-row {
  margin-top: 10px; display: inline-flex; align-items: center; gap: 6px;
  background: ${dark ? 'rgba(99,102,241,0.15)' : '#eef2ff'}; color: ${accent};
  border: 1.5px solid ${dark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}; border-radius: 6px;
  padding: 6px 14px; font-size: 12px; font-weight: 600;
  font-family: 'Barlow', sans-serif; cursor: pointer;
  transition: background .15s;
}
.btn-add-row:hover { background: ${dark ? 'rgba(99,102,241,0.25)' : '#e0e7ff'}; }

.zip-drop-area {
  border: 2px dashed ${dark ? 'rgba(255,255,255,0.2)' : '#c7d2fe'};
  border-radius: 12px;
  padding: 36px 20px;
  text-align: center;
  cursor: pointer;
  background: ${dropBg};
  position: relative;
  transition: all .18s;
}
.zip-drop-area:hover, .zip-drop-area.drag {
  border-color: ${accent}; background: ${dropHoverBg};
}
.zip-drop-area input[type=file] {
  position: absolute; inset: 0;
  opacity: 0; cursor: pointer; width: 100%; height: 100%;
}
.zip-icon-row {
  display: flex; align-items: center; justify-content: center; gap: 8px;
  margin-bottom: 10px;
}
.zip-drop-main { font-size: 14px; font-weight: 700; color: ${accent}; margin-bottom: 3px; }
.zip-drop-hint { font-size: 12px; color: ${textSub}; }

.zip-chip {
  display: flex; align-items: center; gap: 10px;
  background: ${dark ? 'rgba(99,102,241,0.15)' : '#eef2ff'}; border: 1.5px solid ${dark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'};
  border-radius: 10px; padding: 10px 14px; margin-top: 2px;
}
.zip-chip-info { flex: 1; min-width: 0; }
.zip-chip-name { font-size: 13px; font-weight: 600; color: ${accent}; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.zip-chip-size { font-size: 11px; color: ${textSub}; margin-top: 2px; }
.zip-chip-remove {
  background: none; border: none; cursor: pointer; color: #dc2626;
  font-size: 16px; padding: 4px; border-radius: 4px;
  display: flex; align-items: center; transition: background .15s;
}
.zip-chip-remove:hover { background: rgba(220,38,38,0.15); }

.txt-drop-area {
  border: 2px dashed ${dark ? 'rgba(255,255,255,0.2)' : '#c7d2fe'}; border-radius: 10px;
  padding: 18px 16px; text-align: center; cursor: pointer;
  background: ${dropBg}; position: relative; transition: all .18s;
}
.txt-drop-area:hover, .txt-drop-area.drag { border-color: ${accent}; background: ${dropHoverBg}; }
.txt-drop-area input[type=file] {
  position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%;
}
.txt-drop-main { font-size: 13px; font-weight: 600; color: ${accent}; margin-top: 6px; }
.txt-drop-hint { font-size: 11px; color: ${textSub}; margin-top: 2px; }

.chip-list { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; }
.chip {
  display: inline-flex; align-items: center; gap: 5px;
  background: ${dark ? 'rgba(99,102,241,0.15)' : '#eef2ff'}; color: ${accent};
  border: 1px solid ${dark ? 'rgba(99,102,241,0.3)' : '#c7d2fe'}; border-radius: 20px;
  padding: 3px 10px 3px 8px; font-size: 11px; font-weight: 500; max-width: 260px;
}
.chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.chip button { background: none; border: none; cursor: pointer; color: ${accent}; font-size: 13px; line-height: 1; padding: 0; }

.file-tree {
  background: ${dark ? '#162a50' : '#f8fafc'}; border-radius: 10px;
  border: 1.5px solid ${cardBorder};
  padding: 14px 16px; margin-top: 12px;
  font-family: 'Courier New', monospace;
}
.ft-title {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .7px; color: ${textSub}; margin-bottom: 10px;
}
.ft-root { font-size: 12px; color: ${text}; line-height: 1; margin-bottom: 4px; display: flex; align-items: center; gap: 5px; }
.ft-line { display: flex; align-items: flex-start; margin: 2px 0; }
.ft-branch { font-size: 12px; color: ${accent}; white-space: pre; user-select: none; flex-shrink: 0; margin-right: 2px; }
.ft-item  { font-size: 12px; color: ${text}; line-height: 1.6; }
.ft-item.folder { color: #60a5fa; font-weight: 600; }
.ft-item.note   { color: ${textSub}; font-style: italic; }
.ft-item.green  { color: #4ade80; }
.ft-item.purple { color: #c084fc; }
.ft-item.orange { color: #fb923c; }
.ft-item.pink   { color: #f87171; }

.info-banner {
  display: flex; align-items: flex-start; gap: 10px;
  background: ${dark ? '#182038' : '#f5f7ff'}; border: 1px solid ${dark ? '#6366f1' : '#c7d2fe'};
  border-radius: 8px; padding: 12px 14px; margin-bottom: 16px;
}
.info-banner-icon { font-size: 15px; flex-shrink: 0; margin-top: 1px; }
.info-banner-text { font-size: 12px; color: ${dark ? '#c7d2fe' : '#3730a3'}; line-height: 1.6; }
.info-banner-text strong { font-weight: 700; }

.comp-num-badge {
  width: 26px; height: 26px;
  background: ${accent}; color: #fff; border-radius: 50%;
  font-size: 11px; font-weight: 700;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}

.counter {
  display: inline-flex; align-items: center;
  border: 1.5px solid ${inputBorder}; border-radius: 8px; overflow: hidden;
}
.counter button {
  width: 32px; height: 36px; background: ${inputBg}; border: none; cursor: pointer;
  font-size: 16px; color: ${text}; display: flex; align-items: center; justify-content: center;
  transition: background .15s;
}
.counter button:hover { background: ${dark ? 'rgba(99,102,241,0.2)' : '#eef2ff'}; color: ${accent}; }
.counter span {
  width: 44px; text-align: center; font-size: 14px; font-weight: 600; color: ${text};
  border-left: 1px solid ${inputBorder}; border-right: 1px solid ${inputBorder}; padding: 8px 0;
}
.comp-title-row { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }

.format-box {
  background: ${dark ? '#162a50' : '#f9fafb'}; border: 1px solid ${cardBorder};
  border-radius: 8px; padding: 12px 14px; margin-top: 12px;
}
.format-box .fb-label {
  font-size: 10px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .6px; color: ${textSub}; margin-bottom: 8px;
}
.format-box pre { margin: 0; font-size: 12px; color: ${text}; line-height: 1.6; }

.method-info {
  display: flex; align-items: flex-start; gap: 10px;
  background: ${dark ? '#182038' : '#f5f7ff'}; border: 1px solid ${dark ? '#6366f1' : '#c7d2fe'}; border-radius: 9px;
  padding: 10px 14px; margin-bottom: 14px;
  font-size: 13px; color: ${dark ? '#c7d2fe' : '#3730a3'}; line-height: 1.6;
}
.method-info-icon { font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.method-area {
  width: 100%; min-height: 240px; resize: vertical;
  border: 1.5px solid ${inputBorder}; border-radius: 10px;
  padding: 14px 16px; font-size: 14px; line-height: 1.85;
  color: ${text}; font-family: 'Barlow', sans-serif;
  background: ${inputBg}; outline: none; box-sizing: border-box;
  transition: border-color .15s, box-shadow .15s;
}
.method-area:focus {
  border-color: ${accent};
  box-shadow: 0 0 0 3px rgba(99,102,241,.18);
}
.method-meta {
  display: flex; align-items: center; justify-content: space-between;
  margin-top: 7px; padding: 0 2px;
}
.method-word-count { font-size: 11px; color: ${textSub}; font-variant-numeric: tabular-nums; }
.method-clear {
  background: none; border: none; cursor: pointer;
  font-size: 11px; color: #f87171; font-weight: 700; padding: 2px 6px;
  border-radius: 5px; transition: background .15s;
}
.method-clear:hover { background: rgba(248,113,113,0.15); }
.method-tip {
  margin-top: 10px; font-size: 12px; color: ${dark ? '#cbd5e1' : '#5a6b80'};
  background: ${dark ? '#2a2215' : '#fffbeb'}; border: 1px solid ${dark ? '#78350f' : '#fde68a'};
  border-radius: 8px; padding: 8px 12px; line-height: 1.6;
}

.submit-bar {
  position: fixed; bottom: 0; left: 0; right: 0;
  background: ${cardBg}; border-top: 1px solid ${cardBorder};
  padding: 14px 32px; display: flex; align-items: center; justify-content: flex-end; gap: 12px;
  box-shadow: 0 -2px 12px rgba(0,0,0,0.2); z-index: 100;
}
.btn-submit {
  background: ${accent}; color: #fff; border: none; border-radius: 8px;
  padding: 12px 36px; font-size: 14px; font-weight: 600;
  font-family: 'Barlow', sans-serif; cursor: pointer;
  display: flex; align-items: center; gap: 8px;
  transition: background .18s, transform .15s;
}
.btn-submit:hover:not(:disabled) { background: #4f46e5; transform: translateY(-1px); }
.btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }

.submit-error {
  font-size: 12px; font-weight: 600; color: #f87171;
  background: ${dark ? '#3c1818' : '#fee2e2'}; border: 1px solid ${dark ? '#7f1d1d' : '#fca5a5'};
  border-radius: 7px; padding: 7px 12px;
}

@keyframes mrna-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

.submit-success {
  font-size: 12px; font-weight: 600; color: #15803d;
  background: ${dark ? 'rgba(34,197,94,0.15)' : '#f0fdf4'}; border: 1px solid ${dark ? '#166534' : '#86efac'};
  border-radius: 7px; padding: 7px 12px; display: flex; align-items: center; gap: 8px;
}
`;
}



function fmtSize(bytes) {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
 
/* ══ SVG Icons ══ */
function IconFolder({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M3 7c0-1.1.9-2 2-2h4l2 2h8c1.1 0 2 .9 2 2v8c0 1.1-.9 2-2 2H5c-1.1 0-2-.9-2-2V7z"
        fill="#f59e0b" stroke="#d97706" strokeWidth="1" />
    </svg>
  );
}

function IconArrow() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5">
      <path d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  );
}

function IconZip({ size = 30 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="2" width="16" height="20" rx="2" fill="#22c55e" stroke="#16a34a" strokeWidth="1" />
      <rect x="9" y="2" width="6" height="3" rx="1" fill="#15803d" />
      <rect x="10" y="6"  width="4" height="1.5" fill="#bbf7d0" />
      <rect x="10" y="9"  width="4" height="1.5" fill="#bbf7d0" />
      <rect x="10" y="12" width="4" height="1.5" fill="#bbf7d0" />
      <text x="12" y="19" textAnchor="middle" fontSize="5" fontWeight="900" fill="white" fontFamily="monospace">ZIP</text>
    </svg>
  );
}

function IconTxt({ size = 26 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <rect x="4" y="2" width="16" height="20" rx="2" fill="#f3f4f6" stroke="#9ca3af" strokeWidth="1.2" />
      <line x1="8" y1="8"  x2="16" y2="8"  stroke="#6b7280" strokeWidth="1.4" />
      <line x1="8" y1="12" x2="16" y2="12" stroke="#6b7280" strokeWidth="1.4" />
      <line x1="8" y1="16" x2="13" y2="16" stroke="#6b7280" strokeWidth="1.4" />
    </svg>
  );
}

/* ══ ZIP Upload Zone ══ */
function ZipUploadZone({ label, hint, value, onChange }) {
  const [drag, setDrag] = useState(false);
  const handle = (files) => { if (files?.length) onChange(files[0]); };
  return (
    <div>
      {!value ? (
        <div
          className={`zip-drop-area${drag ? " drag" : ""}`}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
        >
          <input type="file" accept=".zip" onChange={(e) => handle(e.target.files)} />
          <div className="zip-icon-row">
            <IconFolder size={30} />
            <IconArrow />
            <IconZip size={30} />
          </div>
          <div className="zip-drop-main">{label}</div>
          {hint && <div className="zip-drop-hint">{hint}</div>}
        </div>
      ) : (
        <div className="zip-chip">
          <IconZip size={32} />
          <div className="zip-chip-info">
            <div className="zip-chip-name">{value.name}</div>
            <div className="zip-chip-size">{fmtSize(value.size)}</div>
          </div>
          <button className="zip-chip-remove" onClick={() => onChange(null)}>✕</button>
        </div>
      )}
    </div>
  );
}

/* ══ TXT Upload Zone ══ */
function UploadZone({ label, hint, accept, value, onChange, multiple = false }) {
  const [drag, setDrag] = useState(false);
  const handle = (files) => {
    if (!files?.length) return;
    multiple ? onChange([...(value || []), ...Array.from(files)]) : onChange(files[0]);
  };
  const remove = (i) => {
    if (multiple) { const n = [...value]; n.splice(i, 1); onChange(n); }
    else onChange(null);
  };
  const chips = multiple ? (value || []) : value ? [value] : [];
  return (
    <div>
      <div
        className={`txt-drop-area${drag ? " drag" : ""}`}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files); }}
      >
        <input type="file" accept={accept} multiple={multiple} onChange={(e) => handle(e.target.files)} />
        <IconTxt size={26} />
        <div className="txt-drop-main">{label}</div>
        {hint && <div className="txt-drop-hint">{hint}</div>}
      </div>
      {chips.length > 0 && (
        <div className="chip-list">
          {chips.map((f, i) => (
            <div key={i} className="chip">
              <span>📄 {f.name}</span>
              <button onClick={() => remove(i)}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ══ Text Field ══ */
function Field({ label, req, placeholder, value, onChange, type = "text" }) {
  return (
    <div className="field">
      <label>{label}{req && <span className="req">*</span>}</label>
      <input type={type} placeholder={placeholder || ""} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

/* ══ File Tree ══ */
function FileTree({ title, lines }) {
  return (
    <div className="file-tree">
      <div className="ft-title">{title}</div>
      {lines.map((l, i) => {
        if (l.type === "root") return (
          <div key={i} className="ft-root">
            <span>📁</span>
            <span style={{ color: "#2563eb", fontWeight: 700 }}>{l.text}</span>
          </div>
        );
        return (
          <div key={i} className="ft-line">
            <span className="ft-branch">{l.branch}</span>
            <span className={`ft-item ${l.cls || ""}`}>{l.text}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ══ Default software entries ══ */
const DEFAULT_SOFTWARE = [
  { item: "Quality Control",                software: "FastQC",                 version: "0.12.1"         },
  { item: "Trimming",                       software: "Trim Galore",            version: "0.6.4"          },
  { item: "Mapping",                        software: "HISAT2",                 version: "2.1.0"          },
  { item: "Differentially Expressed Genes", software: "EdgeR, Limma",           version: "3.42.0, 3.56.0" },
  { item: "GSEA",                           software: "topGO, clusterProfiler",  version: "2.54.0, 4.10.0" },
];

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
export default function MRNAReportUpload({ setReportUrls, setActiveTab, dark }) {
  React.useEffect(() => {
    injectStyle(dark);
    return () => {
      const s = document.getElementById("mrna-style");
      if (s) s.remove();
    };
  }, [dark]);

  const [success,    setSuccess]    = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState("");
  const [reportUrl,  setReportUrl]  = useState("");
  /* Section 06 — Deliverables / Resources ZIP */
  const [resourcesZip, setResourcesZip] = useState(null);
  const [expressionZip, setExpressionZip] = useState(null);

  /* Section 01 — Client info */
  const [s1, setS1] = useState({
    reportTitle: "", clientName: "", institution: "",
    projectId: "", salesPerson: "", contactEmail: "",
  });
  const u1 = (k) => (v) => setS1((p) => ({ ...p, [k]: v }));

  /* Section 02 — Project info */
  const [s2, setS2] = useState({ species: "", sampleFile: null, numComp: 1, compTitles: [""] });

  /* Software rows — editable, pre-filled with defaults */
  const [softwareRows, setSoftwareRows] = useState(DEFAULT_SOFTWARE.map(r => ({ ...r })));

  const addSoftwareRow = () =>
    setSoftwareRows(p => [...p, { item: "", software: "", version: "" }]);

  const removeSoftwareRow = (i) =>
    setSoftwareRows(p => p.filter((_, idx) => idx !== i));

  const editSoftwareRow = (i, key, val) =>
    setSoftwareRows(p => p.map((r, idx) => idx === i ? { ...r, [key]: val } : r));

  const setNumComp = (n) => {
    const count = Math.max(1, Math.min(20, n));
    const titles = [...s2.compTitles];
    while (titles.length < count) titles.push("");
    setS2((p) => ({ ...p, numComp: count, compTitles: titles.slice(0, count) }));
  };
  const setCompTitle = (i, v) => {
    const t = [...s2.compTitles]; t[i] = v;
    setS2((p) => ({ ...p, compTitles: t }));
  };

  /* Section 03 – QC ZIP */
  const [qcZip,      setQcZip]      = useState(null);
  /* Section 04 – Mapping ZIP + reference info */
  const [mappingZip, setMappingZip] = useState(null);
  const [refLink,    setRefLink]    = useState("");
  const [mappingInfoFile, setMappingInfoFile] = useState(null);
  /* Section 05 – Single Comparison.zip */
  const [compZip,    setCompZip]    = useState(null);
  /* Section 06 – Methodology */
  const [methodologyText, setMethodologyText] = useState(DEFAULT_METHODOLOGY);

  /* ── Submit ── */
  const API_BASE = process.env.REACT_APP_API_URL || "";
  async function submitReport() {
    setLoading(true);
    setError("");
    setSuccess(false);

    const form = new FormData();
    form.append("reportTitle",  s1.reportTitle || "");
    form.append("clientName",   s1.clientName || "");
    form.append("institution",  s1.institution || "");
    form.append("projectId",    s1.projectId || "");
    form.append("salesPerson",  s1.salesPerson || "");
    form.append("contactEmail", s1.contactEmail || "");
    form.append("species",      s2.species || "");
    form.append("refLink",      refLink || "");
    form.append("methodologyText", methodologyText || "");
    form.append("softwareRows", JSON.stringify(softwareRows));
    (s2.compTitles || []).map(t => (t || "").trim()).filter(Boolean).forEach(t => form.append("compTitles[]", t));
    if (s2.sampleFile)     form.append("sampleFile",     s2.sampleFile);
    if (qcZip)             form.append("qcZip",          qcZip);
    if (mappingZip)        form.append("mappingZip",     mappingZip);
    if (mappingInfoFile)   form.append("mappingInfoFile", mappingInfoFile);
    if (compZip)           form.append("compZip",        compZip);
    if (expressionZip)     form.append("expressionZip",  expressionZip);
    if (resourcesZip)      form.append("resourcesZip",   resourcesZip);

    try {
      const res  = await fetch(`${API_BASE}/api/mrna/generate`, { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || "Server error — check backend logs");

      const fullReportUrl = `${API_BASE}${data.reportUrl}`;
      const downloadFilename = data.filename || `${s1.projectId || "mRNA_Report"}.html`;
      const downloadUrl = `${API_BASE}/api/mrna/download/${encodeURIComponent(data.filename)}`;

      setReportUrl(fullReportUrl);
      setSuccess(true);

      // Automatically trigger HTML report file download directly
      const dlLink = document.createElement("a");
      dlLink.href = downloadUrl;
      dlLink.download = downloadFilename;
      document.body.appendChild(dlLink);
      dlLink.click();
      setTimeout(() => {
        if (document.body.contains(dlLink)) {
          document.body.removeChild(dlLink);
        }
      }, 500);

      // Automatically open HTML report in a new browser tab for immediate full viewing
      window.open(fullReportUrl, "_blank");

      if (setReportUrls) {
        setReportUrls({
          html: data.reportUrl,
          qcExcel: "",
          mappingExcel: "",
          variantExcel: ""
        });
      }
    } catch (err) {
      console.error("[mRNA] Upload error:", err);
      setError(err.message || "Failed to connect to backend server. Please verify backend is running.");
    } finally {
      setLoading(false);
    }
  }

  const wordCount = methodologyText.trim().split(/\s+/).filter(Boolean).length;

  /* ── Main form ── */
  return (
    <div className="mrna-page">
      <div className="mrna-content">

        {/* ══ 01 CLIENT INFO ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Client &amp; Report Info</h3>
              <p>Appears on the report cover page</p>
            </div>
          </div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label>REPORT TITLE <span className="req">*</span></label>
            <input type="text" placeholder="mRNA Ref Based Report"
              value={s1.reportTitle} onChange={(e) => u1("reportTitle")(e.target.value)} />
          </div>
          <div className="g2" style={{ marginBottom: 16 }}>
            <Field label="CLIENT NAME" req placeholder="e.g. Mr. Pasupathi" value={s1.clientName} onChange={u1("clientName")} />
            <Field label="INSTITUTION" placeholder="e.g. BDU" value={s1.institution} onChange={u1("institution")} />
          </div>
          <div className="g2" style={{ marginBottom: 16 }}>
            <Field label="PROJECT ID" req placeholder="e.g. OO-2024-001" value={s1.projectId} onChange={u1("projectId")} />
            <Field label="SALESPERSON" placeholder="e.g. Gowri Shankar J" value={s1.salesPerson} onChange={u1("salesPerson")} />
          </div>
          <Field label="CONTACT EMAIL" req type="email" placeholder="client@email.com" value={s1.contactEmail} onChange={u1("contactEmail")} />
        </div>

        {/* ══ 02 PROJECT INFO ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Project Information</h3>
              <p>Sample list, species, and comparison groups</p>
            </div>
          </div>

          {/* Species */}
          <div style={{ marginBottom: 20 }}>
            <Field label="SPECIES" req placeholder="e.g. Oryza sativa, Homo sapiens"
              value={s2.species} onChange={(v) => setS2((p) => ({ ...p, species: v }))} />
          </div>

          <div className="divider" />

          {/* ── Editable Software Table ── */}
          <div className="sub-label">Bioinformatics Software Used</div>
          <div className="sw-table-wrap">
            <table className="sw-table">
              <thead>
                <tr>
                  <th style={{ width: "42%" }}>Analysis Item</th>
                  <th style={{ width: "30%" }}>Software</th>
                  <th style={{ width: "20%" }}>Version</th>
                  <th style={{ width: "8%"  }}></th>
                </tr>
              </thead>
              <tbody>
                {softwareRows.map((row, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        value={row.item}
                        placeholder="e.g. Quality Control"
                        onChange={e => editSoftwareRow(i, "item", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        value={row.software}
                        placeholder="e.g. FastQC"
                        onChange={e => editSoftwareRow(i, "software", e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        value={row.version}
                        placeholder="e.g. 0.12.1"
                        onChange={e => editSoftwareRow(i, "version", e.target.value)}
                      />
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        className="sw-remove-btn"
                        title="Remove row"
                        onClick={() => removeSoftwareRow(i)}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className="btn-add-row" onClick={addSoftwareRow}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#228B22" strokeWidth="2.5">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            Add Row
          </button>

          <div className="divider" />

          {/* Sample file + comparisons */}
          <div className="g2">
            <div>
              <div className="sub-label">Sample / Condition File</div>
              <UploadZone label="Drop Sample_info.txt here" hint="Tab-separated · Sample ID · Condition"
                accept=".txt,.tsv" value={s2.sampleFile}
                onChange={(v) => setS2((p) => ({ ...p, sampleFile: v }))} />
              <div className="format-box">
                <div className="fb-label">File Format</div>
                <pre>{`Sample\tCondition\nTest1\tX\nTest2\tY\nTest3\tZ`}</pre>
              </div>
            </div>
            <div>
              <div className="sub-label">Comparison Groups</div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".5px", color: "#374151", display: "block", marginBottom: 6 }}>
                  NUMBER OF COMPARISONS
                </label>
                <div className="counter">
                  <button onClick={() => setNumComp(s2.numComp - 1)}>−</button>
                  <span>{s2.numComp}</span>
                  <button onClick={() => setNumComp(s2.numComp + 1)}>+</button>
                </div>
              </div>
              {s2.compTitles.map((t, i) => (
                <div key={i} className="comp-title-row">
                  <div className="comp-num-badge">{i + 1}</div>
                  <input
                    type="text"
                    placeholder="e.g. ICD vs DCD"
                    value={t}
                    onChange={(e) => setCompTitle(i, e.target.value)}
                    style={{ flex: 1, padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, fontFamily: "Roboto, sans-serif", outline: "none", color: "#111827", background: "#fff" }}
                    onFocus={(e) => { e.target.style.borderColor = "#228B22"; e.target.style.boxShadow = "0 0 0 3px rgba(34,139,34,.1)"; }}
                    onBlur={(e)  => { e.target.style.borderColor = "#e5e7eb";  e.target.style.boxShadow = "none"; }}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ══ 03 QC ZIP ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Quality Control (QC)</h3>
              <p>Upload QC.zip — FastQC/MultiQC stats &amp; quality image plots</p>
            </div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">ℹ️</div>
            <div className="info-banner-text">
              Compress the <strong>QC/</strong> folder directly as <strong>QC.zip</strong> keeping the exact file names shown below.
            </div>
          </div>
          <ZipUploadZone
            label="Drop QC.zip here or click to browse"
            hint="QC.zip — 6 text/tsv files + 4 PNG images"
            value={qcZip}
            onChange={setQcZip}
          />
          <FileTree title="QC.zip — exact file names expected" lines={[
            { type: "root", text: "QC/" },
            { branch: "├── ", text: "multiqc_general_stats_raw.txt",                    cls: "green"  },
            { branch: "├── ", text: "multiqc_general_stats_trim.txt",                   cls: "green"  },
            { branch: "├── ", text: "fastqc_per_sequence_quality_scores_plot_raw.tsv",  cls: "green"  },
            { branch: "├── ", text: "fastqc_per_sequence_quality_scores_plot_trim.tsv", cls: "green"  },
            { branch: "├── ", text: "fastqc_sequence_counts_plot_raw.txt",              cls: "green"  },
            { branch: "├── ", text: "fastqc_sequence_counts_plot_trim.txt",             cls: "green"  },
            { branch: "├── ", text: "fastqc_per_sequence_gc_content_plot_raw.png",      cls: "purple" },
            { branch: "├── ", text: "fastqc_per_sequence_gc_content_plot_trim.png",     cls: "purple" },
            { branch: "├── ", text: "fastqc_per_base_sequence_quality_plot_raw.png",    cls: "purple" },
            { branch: "└── ", text: "fastqc_per_base_sequence_quality_plot_trim.png",   cls: "purple" },
          ]} />
        </div>

        {/* ══ 04 MAPPING ZIP ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Mapping</h3>
              <p>Upload Mapping.zip — one subfolder per sample, each containing genome_results.txt</p>
            </div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">ℹ️</div>
            <div className="info-banner-text">
              Compress the <strong>Mapping/</strong> folder as <strong>Mapping.zip</strong>.
              Each sample subfolder must contain <strong>genome_results.txt</strong>.
            </div>
          </div>
          <ZipUploadZone
            label="Drop Mapping.zip here or click to browse"
            hint="Mapping.zip — one subfolder per sample with genome_results.txt"
            value={mappingZip}
            onChange={setMappingZip}
          />
          <FileTree title="Mapping.zip — expected structure" lines={[
            { type: "root", text: "Mapping/" },
            { branch: "├── ", text: "DC1/",                   cls: "folder" },
            { branch: "│   └── ", text: "genome_results.txt", cls: "orange" },
            { branch: "├── ", text: "DC2/",                   cls: "folder" },
            { branch: "│   └── ", text: "genome_results.txt", cls: "orange" },
            { branch: "├── ", text: "DC3/",                   cls: "folder" },
            { branch: "│   └── ", text: "genome_results.txt", cls: "orange" },
            { branch: "└── ", text: "… (one folder per sample)", cls: "note" },
          ]} />
          <div className="divider" />
          <div className="sub-label">Reference Genome</div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label>REFERENCE LINK / PATH</label>
            <input type="url" placeholder="https://ensembl.org/... or /ref/genome/path"
              value={refLink} onChange={(e) => setRefLink(e.target.value)} />
          </div>

          <div className="sub-label">Reference Genome Statistics (optional)</div>
          <UploadZone
            label="Drop mapping_info.txt here"
            hint="Tab-separated · Stat name · Value (e.g. Total Scaffold length, N50)"
            accept=".txt,.tsv"
            value={mappingInfoFile}
            onChange={setMappingInfoFile}
          />
          <div className="format-box">
            <div className="fb-label">File Format</div>
            <pre>{`Total Scaffold length\t719,894,357\nNo.of scaffolds\t65\nN50\t71,215,609`}</pre>
          </div>
        </div>

        {/* ══ 05 COMPARISON ZIP ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Differential Expression &amp; Comparisons</h3>
              <p>Upload Comparison.zip — volcano, MA, heatmaps, PCA, DEG counts for all groups</p>
            </div>
          </div>
           <div className="info-banner">
             <div className="info-banner-icon">ℹ️</div>
             <div className="info-banner-text">
               Compress <strong>all</strong> comparison folders into a <strong>single Comparison.zip</strong>.
                Each subfolder can contain:
             <strong> DEG/</strong>, <strong>Expression counts/</strong> and <strong>GSEA/</strong> (or enrichment files placed directly inside).
               For GSEA, file naming is flexible for both plots (.png, .jpg) and enrichment tables (.xlsx, .xls, .csv, .tsv). Uploaded GSEA Excel and CSV tables are displayed directly with all columns and rows preserved.
             </div>
           </div>
          <ZipUploadZone
            label="Drop Comparison.zip here or click to browse"
            hint="One ZIP containing ALL comparison folders"
            value={compZip}
            onChange={setCompZip}
          />

          {/* Dynamic file tree */}
          {(() => {
            const lines = [{ type: "root", text: "Comparison/" }];
            s2.compTitles.forEach((t, i) => {
              const lbl    = t.trim() || `Comparison_${i + 1}`;
              const isLast = i === s2.compTitles.length - 1;
              const pre    = isLast ? "└── " : "├── ";
              const pipe   = isLast ? "    " : "│   ";
              lines.push({ branch: pre,               text: `${lbl}/`,                             cls: "folder" });
              lines.push({ branch: `${pipe}├── `,     text: "DEG/",                               cls: "folder" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Boxplot_before_normalization.png",   cls: "purple" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Boxplot_before_normalization.pdf",   cls: "pink"   });
              lines.push({ branch: `${pipe}│   ├── `, text: "Boxplot_after_normalization.png",    cls: "purple" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Boxplot_after_normalization.pdf",    cls: "pink"   });
              lines.push({ branch: `${pipe}│   ├── `, text: "PCA_plot.jpeg",                      cls: "purple" });
              lines.push({ branch: `${pipe}│   ├── `, text: "PCA_plot.pdf",                       cls: "pink"   });
              lines.push({ branch: `${pipe}│   ├── `, text: "Sample_Distance_Map.png",            cls: "purple" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Sample_Distance_Map.pdf",            cls: "pink"   });
              lines.push({ branch: `${pipe}│   ├── `, text: "Heatmap.png",                        cls: "purple" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Heatmap.pdf",                        cls: "pink"   });
              lines.push({ branch: `${pipe}│   ├── `, text: "Heatmap.xlsx",                       cls: "orange" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Volcano_plot.jpeg",                  cls: "purple" });
              lines.push({ branch: `${pipe}│   ├── `, text: "Volcano_plot.pdf",                   cls: "pink"   });
              lines.push({ branch: `${pipe}│   └── `, text: "DEG.xlsx",                           cls: "orange" });
              lines.push({ branch: `${pipe}├── `,     text: "Expression counts/",                 cls: "folder" });
              lines.push({ branch: `${pipe}│   └── `, text: "… (one .counts per sample)",         cls: "note"   });
              lines.push({ branch: `${pipe}└── `,     text: "GSEA/",                              cls: "folder" });
              lines.push({ branch: `${pipe}    ├── `, text: "GO_BP_top10.png (or any GO / BP / CC / MF plot)", cls: "purple" });
              lines.push({ branch: `${pipe}    ├── `, text: "GO_CC_top10.png (or any GO plot)",   cls: "purple" });
              lines.push({ branch: `${pipe}    ├── `, text: "GO_MF_top10.png (or any GO plot)",   cls: "purple" });
              lines.push({ branch: `${pipe}    ├── `, text: "GO_enrichment_complete.xlsx (or any GO / enrichment .xlsx / .csv)", cls: "orange" });
              lines.push({ branch: `${pipe}    ├── `, text: "KEGG_top20.png (or any KEGG / pathway plot)", cls: "purple" });
              lines.push({ branch: `${pipe}    └── `, text: "KEGG_enrichment_results.xlsx (or any KEGG / pathway .xlsx / .csv)", cls: "orange" });

            });
            return <FileTree title="Comparison.zip — expected structure" lines={lines} />;
          })()}
        </div>
        {/* ══ 06 DATA FILES & DELIVERABLES (RESOURCES TAB) ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Data Files &amp; Deliverables (Resources Tab)</h3>
              <p>Upload a single ZIP containing only the important files you want listed in the Resources → Data Files &amp; Deliverables section of the report (e.g. Expression counts matrix, DEG tables, GSEA results, high-res plots)</p>
            </div>
          </div>
          <div className="info-banner">
            <div className="info-banner-icon">💡</div>
            <div className="info-banner-text">
              <strong>Tip:</strong> Put only final deliverables inside this ZIP (e.g. count matrices, final comparison tables, volcano plots, PCA figures). These files will appear directly in the report's <strong>Resources</strong> tab with individual download buttons and a <strong>Download All (.ZIP)</strong> option.
            </div>
          </div>
          <ZipUploadZone
            label="Drop Deliverables / Resources ZIP here or click to browse"
            hint="One ZIP containing deliverables (Expression counts, DEG tables, plots, etc.)"
            value={resourcesZip}
            onChange={setResourcesZip}
          />
        </div>

        {/* ══ 06 METHODOLOGY ══ */}
        <div className="section-card">
          <div className="section-header">
            <div className="section-header-text">
              <h3>Methodology &amp; Software Pipeline</h3>
              <p>Edit software tool details and methodology description for the report</p>
            </div>
          </div>

          <div className="method-info">
            <span className="method-info-icon">📋</span>
            Pre-filled from your standard mRNA RNA-Seq protocol. Edit freely — every line you type
            will be displayed exactly as written in the report.
          </div>

          <div style={{ position: "relative" }}>
            <textarea
              value={methodologyText}
              onChange={(e) => setMethodologyText(e.target.value)}
              className="method-area"
              placeholder="Enter your methodology text here…"
              spellCheck={true}
            />
            <div className="method-meta">
              <span className="method-word-count">
                {wordCount.toLocaleString()} words · {methodologyText.length.toLocaleString()} characters
              </span>
              <button
                className="method-clear"
                onClick={() => setMethodologyText("")}
                title="Clear all text"
              >✕ Clear</button>
            </div>
          </div>

          <div className="method-tip">
            <strong>Tip:</strong> Start a line with a heading (e.g. <em>"Bioinformatic Analyses"</em>) —
            the report will render it as a bold section header automatically.
          </div>
        </div>

      </div>

      {/* ══ SUBMIT BAR ══ */}
      <div className="submit-bar">
        {error && <div className="submit-error">⚠ {error}</div>}
        {success && reportUrl && (
          <div className="submit-success">
            ✓ Report Generated!
            <a
              href={reportUrl}
              target="_blank"
              rel="noreferrer"
              style={{ marginLeft: 8, color: "#15803d", fontWeight: 700, textDecoration: "underline" }}
            >
              Open Report ↗
            </a>
          </div>
        )}
        <button className="btn-submit" onClick={submitReport} disabled={loading}>
          {loading ? (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5"
                style={{ animation: "mrna-spin 0.9s linear infinite" }}>
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
              Generating Report…
            </>
          ) : (
            <>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              Upload &amp; Generate Report
            </>
          )}
        </button>
      </div>

    </div>
  );
}